import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  IoCalendarOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoChevronDownOutline
} from 'react-icons/io5';

const DateField = ({
  label,
  name,
  value,
  onChange,
  error,
  required = false,
  autoComplete,
  placeholder = 'Select a date',
  minDate,
  maxDate,
  icon
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [viewDate, setViewDate] = useState(value ? new Date(value) : new Date());
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [showYearSelector, setShowYearSelector] = useState(false);

  // ✅ portal positioning (prevents clipping + keeps clicks working everywhere)
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0, width: 328, placement: 'bottom' });

  const pickerRef = useRef(null);
  const dateFieldRef = useRef(null);

  // Format the date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Generate days for the month
  const getDaysInMonth = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();

    const days = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthDays - i, month: month - 1, year, isCurrentMonth: false });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, month, year, isCurrentMonth: true });
    }

    const remainingCells = 42 - days.length;
    for (let i = 1; i <= remainingCells; i++) {
      days.push({ day: i, month: month + 1, year, isCurrentMonth: false });
    }

    return days;
  };

  const getMonths = () => [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 100; i <= currentYear; i++) years.push(i);
    return years;
  };

  const handleMonthSelect = (monthIndex) => {
    setViewDate(new Date(viewDate.getFullYear(), monthIndex, 1));
    setShowMonthSelector(false);
  };

  const handleYearSelect = (year) => {
    setViewDate(new Date(year, viewDate.getMonth(), 1));
    setShowYearSelector(false);
  };

  const isDateSelected = (day, month, year) => {
    if (!value) return false;
    const selectedDate = new Date(value);
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === month &&
      selectedDate.getFullYear() === year
    );
  };

  const isToday = (day, month, year) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === month &&
      today.getFullYear() === year
    );
  };

  const isDateDisabled = (day, month, year) => {
    const date = new Date(year, month, day);

    if (minDate) {
      const min = new Date(minDate);
      if (date < min) return true;
    }

    if (maxDate) {
      const max = new Date(maxDate);
      if (date > max) return true;
    }

    return false;
  };

  const handleSelectDate = (day, month, year) => {
    if (isDateDisabled(day, month, year)) return;

    // Fix timezone issues by using UTC methods to ensure correct date
    const selectedDate = new Date(Date.UTC(year, month, day));
    const formattedDate = selectedDate.toISOString().split('T')[0]; // YYYY-MM-DD

    onChange({ target: { name, value: formattedDate } });
    setShowPicker(false);
  };

  const goToPrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const toggleMonthSelector = (e) => {
    e.stopPropagation();
    setShowYearSelector(false);
    setShowMonthSelector(!showMonthSelector);
  };

  const toggleYearSelector = (e) => {
    e.stopPropagation();
    setShowMonthSelector(false);
    setShowYearSelector(!showYearSelector);
  };

  const computePickerPosition = () => {
    if (!dateFieldRef.current) return;

    const rect = dateFieldRef.current.getBoundingClientRect();

    const gutter = 12;
    const maxWidth = 328; // ~20.5rem
    const minWidth = 260;

    const width = Math.min(maxWidth, Math.max(minWidth, rect.width));
    let left = rect.left;

    if (left + width > window.innerWidth - gutter) left = window.innerWidth - gutter - width;
    if (left < gutter) left = gutter;

    const pickerHeightEstimate = 360;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement = spaceBelow < pickerHeightEstimate ? 'top' : 'bottom';

    const top = placement === 'top' ? rect.top - 8 : rect.bottom + 8;

    setPickerPos({ top, left, width, placement });
  };

  const openPicker = () => {
    computePickerPosition();
    setShowPicker(true);
  };

  const closePicker = () => {
    setShowPicker(false);
    setShowMonthSelector(false);
    setShowYearSelector(false);
  };

  const toggleDatePicker = () => {
    if (!showPicker) openPicker();
    else closePicker();
  };

  // ✅ Click outside should consider BOTH the anchor and the portal picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      const anchor = dateFieldRef.current;
      const picker = pickerRef.current;

      const clickedAnchor = anchor && anchor.contains(event.target);
      const clickedPicker = picker && picker.contains(event.target);

      if (!clickedAnchor && !clickedPicker) closePicker();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Reposition while open
  useEffect(() => {
    if (!showPicker) return;

    const onReflow = () => computePickerPosition();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);

    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPicker]);

  const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const borderRing = error
    ? 'border-rose-300 focus:ring-rose-500/25 dark:border-rose-700/60'
    : 'border-slate-200 focus:ring-sky-500/35 dark:border-slate-800';

  const pickerUI = showPicker ? (
    <div
      ref={pickerRef}
      className="fixed z-[9999] rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-950"
      style={{
        width: pickerPos.width,
        left: pickerPos.left,
        top: pickerPos.top,
        transform: pickerPos.placement === 'top' ? 'translateY(-100%)' : 'translateY(0)'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-3 dark:border-slate-800">
        <button
          type="button"
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-slate-100"
          onClick={goToPrevMonth}
          aria-label="Previous month"
        >
          <IoChevronBackOutline className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-900"
            onClick={toggleMonthSelector}
            aria-label="Select month"
          >
            {viewDate.toLocaleDateString('en-US', { month: 'long' })}
            <IoChevronDownOutline className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          </button>

          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-900"
            onClick={toggleYearSelector}
            aria-label="Select year"
          >
            {viewDate.getFullYear()}
            <IoChevronDownOutline className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        <button
          type="button"
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-slate-100"
          onClick={goToNextMonth}
          aria-label="Next month"
        >
          <IoChevronForwardOutline className="h-5 w-5" />
        </button>
      </div>

      {/* Month dropdown */}
      {showMonthSelector && (
        <div className="max-h-64 overflow-y-auto p-2">
          <div className="grid grid-cols-2 gap-1">
            {getMonths().map((month, index) => (
              <button
                key={month}
                type="button"
                className={[
                  'rounded-xl px-3 py-2 text-left text-sm font-medium transition',
                  index === viewDate.getMonth()
                    ? 'bg-sky-600 text-white'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-900'
                ].join(' ')}
                onClick={() => handleMonthSelect(index)}
              >
                {month}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Year dropdown */}
      {showYearSelector && (
        <div className="max-h-64 overflow-y-auto p-2">
          <div className="grid grid-cols-3 gap-1">
            {getYears()
              .slice()
              .reverse()
              .map((year) => (
                <button
                  key={year}
                  type="button"
                  className={[
                    'rounded-xl px-3 py-2 text-sm font-medium transition',
                    year === viewDate.getFullYear()
                      ? 'bg-sky-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-900'
                  ].join(' ')}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Calendar */}
      {!showMonthSelector && !showYearSelector && (
        <>
          <div className="px-3 pt-3">
            <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {weekdays.map((day) => (
                <div key={day} className="py-2">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 pb-3">
              {getDaysInMonth().map((date, index) => {
                const selected = isDateSelected(date.day, date.month, date.year);
                const today = isToday(date.day, date.month, date.year);
                const disabled = isDateDisabled(date.day, date.month, date.year);
                const otherMonth = !date.isCurrentMonth;

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelectDate(date.day, date.month, date.year)}
                    disabled={disabled}
                    className={[
                      'h-10 rounded-xl text-sm transition',
                      'focus:outline-none focus:ring-2 focus:ring-sky-500/30',
                      disabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-slate-100 dark:hover:bg-slate-900',
                      otherMonth ? 'text-slate-400 dark:text-slate-600' : 'text-slate-800 dark:text-slate-200',
                      today && !selected ? 'ring-1 ring-sky-500/40 dark:ring-sky-500/30' : '',
                      selected
                        ? 'bg-sky-600 text-white hover:bg-sky-700 dark:bg-sky-600 dark:hover:bg-sky-500'
                        : ''
                    ].join(' ')}
                  >
                    {date.day}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-3 py-3 dark:border-slate-800">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
              onClick={() => {
                const today = new Date();
                if (!isDateDisabled(today.getDate(), today.getMonth(), today.getFullYear())) {
                  handleSelectDate(today.getDate(), today.getMonth(), today.getFullYear());
                }
              }}
            >
              Today
            </button>

            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-slate-100"
              onClick={() => {
                onChange({ target: { name, value: '' } });
                closePicker();
              }}
            >
              Clear
            </button>
          </div>
        </>
      )}
    </div>
  ) : null;

  return (
    <div className="w-full">
      <label
        htmlFor={name}
        className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200"
      >
        {icon && (
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
            {icon}
          </span>
        )}
        <span className="flex items-center gap-1">
          {label}
          {required && <span className="text-rose-500">*</span>}
        </span>
      </label>

      <div className="relative" ref={dateFieldRef}>
        <button
          type="button"
          onClick={toggleDatePicker}
          className={[
            'group relative flex h-11 w-full items-center justify-between rounded-xl border bg-white px-3 text-left text-sm shadow-sm transition',
            'hover:bg-slate-50',
            'focus:outline-none focus:ring-2',
            borderRing,
            'dark:bg-slate-950 dark:hover:bg-slate-900'
          ].join(' ')}
        >
          <span className="min-w-0">
            {value ? (
              <span className="block truncate font-medium text-slate-900 dark:text-slate-100">
                {formatDateForDisplay(value)}
              </span>
            ) : (
              <span className="block truncate text-slate-400 dark:text-slate-500">
                {placeholder}
              </span>
            )}
          </span>

          <span className="ml-3 inline-flex items-center gap-2">
            <IoCalendarOutline className="h-5 w-5 text-slate-400 transition group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300" />
          </span>
        </button>

        {/* Hidden input for form submission */}
        <input type="hidden" id={name} name={name} value={value || ''} autoComplete={autoComplete} />
      </div>

      {error && (
        <div className="mt-1.5 text-xs font-medium text-rose-600 dark:text-rose-400">
          {error}
        </div>
      )}

      {/* ✅ Render picker into body so it never gets clipped and remains clickable */}
      {typeof document !== 'undefined' ? createPortal(pickerUI, document.body) : null}
    </div>
  );
};

export default DateField;
